import { Link } from 'react-router-dom';
import { categories, getProductsByIds, Product } from '../../../mocks/products';

// 카테고리 배너 데이터
const categoryBanners = [
  {
    id: 'disease',
    ...categories.disease.banner,
    link: '/products#category-disease',
  },
  {
    id: 'growth',
    ...categories.growth.banner,
    link: '/products#category-growth',
  },
  {
    id: 'stress',
    ...categories.stress.banner,
    link: '/products#category-stress',
  },
];

// 메인페이지에 진열할 인기 제품 ID 목록 (여기서만 수정하면 됨)
const popularProductIds = [
  'sak-hwang',
  'gyu-hwang',
  'kelp-amino',
  'gold-kali',
  'glucomin',
  'frost-reduce',
  'aqua-screen',
  'npk',
];

// 제품 카드 컴포넌트
function ProductCard({ product }: { product: Product }) {
  // 용량 표시 로직: volumeOptions가 있으면 정확하게 나열, 없으면 단일 용량 표시
  const volumeDisplay =
    product.volumeOptions && product.volumeOptions.length > 1
      ? product.volumeOptions.join(', ')
      : product.volume;

  return (
    <Link to={`/products/${product.id}`} className="group cursor-pointer">
      {/* 정사각형 썸네일 */}
      <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 md:mb-4">
        <img
          src={product.thumbnail}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      {/* 제품 정보 */}
      <div>
        <p className="text-xs md:text-sm text-emerald-600 mb-1">{product.nameEn}</p>
        <h4 className="text-sm md:text-base font-semibold text-[#222222] mb-1 md:mb-2">
          {product.name}
          <span className="text-xs md:text-sm font-normal text-gray-500 ml-1 md:ml-2">
            {volumeDisplay}
          </span>
        </h4>
        <p className="text-xs md:text-sm text-gray-500 leading-relaxed line-clamp-2">
          {product.recommendation}
        </p>
      </div>
    </Link>
  );
}

export default function ProductsSection() {
  // ID 목록으로 제품 데이터 자동 로드
  const popularProducts = getProductsByIds(popularProductIds);

  return (
    <section className="pt-20 md:pt-44 pb-20 bg-white" data-product-shop>
      <div className="max-w-[1400px] mx-auto px-5">
        {/* 카테고리 배너 섹션 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 md:mb-40">
          {categoryBanners.map((banner) => (
            <Link
              key={banner.id}
              to={banner.link}
              className="relative aspect-square md:aspect-[3/4] rounded-xl overflow-hidden group cursor-pointer"
            >
              <img
                src={banner.image}
                alt={banner.headCopy}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70 md:bg-gradient-to-t md:from-black/70 md:via-black/30 md:to-transparent"></div>
              <div className="absolute top-0 left-0 right-0 p-6 md:p-8 md:top-auto md:bottom-0">
                <h3 className="text-white text-xl md:text-2xl font-bold mb-2 md:mb-3">
                  {banner.headCopy}
                </h3>
                <p className="text-white/80 text-sm md:text-base leading-relaxed mb-4 md:mb-5">
                  {banner.subCopy}
                </p>
                <div className="hidden md:block md:text-left">
                  <span className="inline-flex items-center gap-2 text-white text-sm md:text-base font-medium px-4 md:px-5 py-2 md:py-2.5 rounded-full border border-white hover:bg-white/20 transition-colors whitespace-nowrap">
                    전체보기
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:hidden text-right">
                <span className="inline-flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-full border border-white hover:bg-white/20 transition-colors whitespace-nowrap">
                  전체보기
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* 인기 제품 섹션 */}
        <div className="mb-8 md:mb-12">
          <h2 className="text-xl md:text-3xl font-bold text-[#222222] text-center mb-2 md:mb-3">
            가장 많이 찾는 제품
          </h2>
          <p className="text-sm md:text-base text-gray-500 text-center">
            고객님들이 가장 많이 선택한 베스트 제품을 만나보세요
          </p>
        </div>

        {/* 제품 그리드 - 모바일 2열 6개, PC 4열 8개 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-12 mb-8 md:mb-12">
          {popularProducts.map((product, index) => (
            <div key={product.id} className={index >= 6 ? 'hidden md:block' : ''}>
              <ProductCard product={product} />
            </div>
          ))}
        </div>

        {/* 전체 제품 보러가기 버튼 */}
        <div className="text-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 md:px-8 py-3 bg-[#222222] text-white text-sm md:text-base font-medium rounded-full hover:bg-[#333333] transition-colors cursor-pointer whitespace-nowrap"
          >
            전체 제품 보러가기
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}